import React, { useEffect, useState, useContext } from 'react';

import * as S from './styles';
import { IProps } from './types';
import { Container, Row, Col } from 'react-bootstrap';
import { BreadCrumb, Button, Loading } from '@atoms';
import { TextField, AddressCard } from '@molecules';
import { useOtp } from '@src/hooks/useOtp';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { CUSTOMER_UPDATE, ME_FOR_CONTACT } from '@components/pages/ContactPage/queries';
import { customerUpdate } from '@components/pages/ContactPage/__generated__/customerUpdate';
import { meForContact } from '@components/pages/ContactPage/__generated__/meForContact';
import { useFormik } from 'formik';
import { history } from '@src/history';
import { accountDeleteUrl, accountLoginUrl, accountUrl } from '@src/app/routes';
import { CustomerUpdateInput } from '../../../../__generated__/globalTypes';
import { Link } from 'react-router-dom';
import { useAlert } from 'react-alert';
import { joinErrorMessages } from '@utils';
import { CredentialContext, credentialType } from '@providers';
import { ALPHABET_ONLY_REGEX, MOBILE_NUMBER_REGEX, OTP_REGEX } from '@src/utils/constants';

const ContactPage: React.FC<IProps> = ({}: IProps) => {
  const {
    otpRequested,
    otpVerified,
    setOtpRequested,
    setOtpVerified,
    disableOtpRequest,
    setDisableOtpRequest,
    errors: otpErrors, //TODO: error handling (1 minute rule)
    handleRequestOtp,
    handleVerifyOtp,
  } = useOtp();

  const { currentUser, setCurrentUser } = useContext<credentialType>(CredentialContext);

  useEffect(() => {
    if (otpErrors.length) {
      otpErrors.forEach((err) => {
        // OTP duplicated in a minute error
        alert.error(err.message);
      });
    }
  }, [otpErrors]);

  const { data, error, loading, refetch } = useQuery<meForContact>(ME_FOR_CONTACT, {
    onCompleted: (data) => {
      if (data?.me) {
        const { __typename, mobileVerified, ...values } = data?.me;
        formik.setValues(values);
        setOtpVerified(mobileVerified);

        const copyCurrentUser = JSON.parse(JSON.stringify(currentUser));
        copyCurrentUser.user.firstName = data?.me?.firstName;
        copyCurrentUser.user.lastName = data?.me?.lastName;
        copyCurrentUser.user.mobileNumber = Number(data?.me?.mobileNumber);
        copyCurrentUser.user.mobileVerified = data?.me?.mobileVerified;

        // local user info update
        setCurrentUser(copyCurrentUser);
      } else {
        history.push(accountLoginUrl);
      }
    },
  });

  const alert = useAlert();
  const [updateCustomer] = useMutation<customerUpdate>(CUSTOMER_UPDATE, {
    onCompleted: (data) => {
      const errors = data?.customerUpdate?.errors;
      if (0 < errors?.length) {
        const msg = joinErrorMessages(errors);
        alert.error(msg);
      } else {
        alert.success('User data updated');
        const copyCurrentUser = JSON.parse(JSON.stringify(currentUser));
        copyCurrentUser.user.firstName = data?.customerUpdate?.user?.firstName;
        copyCurrentUser.user.lastName = data?.customerUpdate?.user?.lastName;
        copyCurrentUser.user.mobileNumber = Number(data?.customerUpdate?.user?.mobileNumber);
        copyCurrentUser.user.mobileVerified = data?.customerUpdate?.user?.mobileVerified;

        // local user info update
        setCurrentUser(copyCurrentUser);
        if (!data.customerUpdate.user.mobileVerified) {
          setOtpRequested(false);
          setOtpVerified(false);
        }

        refetch();
      }
    },
  });

  const [mobileNumberChanged, setMobileNumberChanged] = useState(false);
  const formik = useFormik<CustomerUpdateInput>({
    initialValues: {
      firstName: '',
      lastName: '',
      //@ts-ignore: to show placeholder text
      mobileNumber: '',
    },
    onSubmit: (values) => {
      if (!ALPHABET_ONLY_REGEX.test(values.firstName) || !ALPHABET_ONLY_REGEX.test(values.lastName)) {
        alert.error('Only alphabets can be entered in the name');
        return;
      }

      const input = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      };

      if (!MOBILE_NUMBER_REGEX.test(String(values.mobileNumber))) {
        alert.error('Please enter a valid mobile number (10 digit)');
        return;
      }
      if (mobileNumberChanged) {
        input['mobileNumber'] = Number(values.mobileNumber);
      }
      updateCustomer({ variables: { input } });
    },
  });

  const [otpValue, setOtpValue] = useState('');
  const handleVerify = (e: any) => {
    if (!OTP_REGEX.test(otpValue)) {
      alert.error('Please enter a valid OTP (6 digit)');
      return;
    }

    handleVerifyOtp(otpValue, formik.values.mobileNumber.toString(), (data) => {
      setOtpValue('');
    });
  };

  const breadCrumbData = [
    {
      name: 'My Account',
      link: accountUrl,
    },
    {
      name: 'Contact',
    },
  ];

  return (
    (data && (
      <S.Wrapper>
        <Container>
          <BreadCrumb data={breadCrumbData} />
          <S.Subtitle>PERSONAL DATA</S.Subtitle>
          <Row>
            <Col md={6}>
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  name="firstName"
                  type="text"
                  required
                  label="First name"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                />
                <TextField
                  name="lastName"
                  type="text"
                  required
                  label="Last name"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                />
                <TextField
                  name="mobileNumber"
                  type="text"
                  label="Mobile Number"
                  value={formik.values.mobileNumber}
                  disabled={disableOtpRequest}
                  maxLength={10}
                  onChange={(e: any) => {
                    formik.handleChange(e);
                    setMobileNumberChanged(true);
                  }}
                />
                <Button type="submit">Update</Button>
              </form>
            </Col>
            <Col md={6}>
              {otpVerified ? (
                <span>Mobile number verified.</span>
              ) : (
                <>
                  <p>Mobile number is not verified yet.</p>
                  <Button
                    onClick={() => {
                      handleRequestOtp(formik.values.mobileNumber.toString());
                    }}
                  >
                    Send Otp
                  </Button>
                  {otpRequested ? (
                    <>
                      <TextField
                        name="otp-value"
                        type="text"
                        label="OTP value (You can send again after a minute.)"
                        value={otpValue}
                        maxLength={6}
                        onChange={(e: any) => {
                          setOtpValue(e.target.value);
                        }}
                      />
                      <Button onClick={handleVerify}>Verify</Button>
                    </>
                  ) : null}
                </>
              )}
            </Col>
          </Row>
          <Row>
            {data?.me?.addresses?.length == 0 ? (
              <S.NoDataCol>
                <S.NoData>YOU WILL SEE NEW ADDRESSES HERE AFTER FIRST SUCCESSFUL CHECKOUT.</S.NoData>
              </S.NoDataCol>
            ) : (
              <Col md={6}>
                {data?.me?.addresses?.map((address, idx) => {
                  return <AddressCard key={idx} address={address} refetch={refetch} />;
                })}
              </Col>
            )}
          </Row>
          <Row>
            <Col>
              <Link to={accountDeleteUrl}>Delete account</Link>
            </Col>
          </Row>
        </Container>
      </S.Wrapper>
    )) || <Loading />
  );
};

export { ContactPage };
